import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { addCommasToNumber, findImage } from 'utils/Utils';
import styled from 'styled-components';
import LoadingIndicator from 'app/components/LoadingIndicator';
import html2canvas from 'html2canvas';

export interface TransactionReceiptResponse {
  paymentAmtInfo: {
    transAmt: number;
    discountAmt: number;
    vatAmt: number;
    supplyAmt: number;
    taxfreeAmt: number;
    svcAmt: number;
    totalPayAmt: number;
    payCurrCd: string;
    paymentAmtInfoTitle: string;
    totalPayAmtSubTitle: string;
    transAmtSubTitle: string;
    discountAmtSubTitle: string;
    vatAmtSubTitle: string;
    supplyAmtSubTitle: string;
    taxfreeAmtSubTitle: string;
    svcAmtSubTitle: string;
  };
  paymentSchemeInfo: {
    cardNo: string | null;
    schemeNm: string;
    transactType: string;
    transactDttm: string;
    transactUtc: number;
    installmentPeriod: string;
    orderId: string;
    paymentSchemeInfoTitle: string;
    cardNoSubTitle: string;
    schemeSubTitle: string;
    transactTypeSubTitle: string;
    transactDttmSubTitle: string;
    monthlyInstallmentSubTitle: string;
    orderIdSubTitle: string;
    appNoSubTitle: string;
    appNo: string;
  };
  merchantInfo: {
    merchantNm: string;
    merchantCeo: string;
    merchantBizno: string;
    merchantAddr: string;
    merchantContractTel: string;
    merchantContractUrl: string;
    merchantInfoTitle: string;
    merchantNmSubTitle: string;
    merchantCeoSubTitle: string;
    merchantBiznoSubTitle: string;
    merchantAddrSubTitle: string;
    merchantContractTelSubTitle: string;
    merchantContractUrlSubTitle: string;
  };
  paymentGwInfo: {
    paymentGwNm: string;
    paymentGwCeo: string;
    paymentGwBizno: string;
    paymentGwAddr: string;
    paymentGwInfoTitle: string;
    paymentGwNmSubTitle: string;
    paymentGwCeoSubTitle: string;
    paymentGwBiznoSubTitle: string;
    paymentGwAddrSubTitle: string;
  };
  amountDetailUse: 'Y' | 'N';
  defaultSendEmailArrayList: string[] | null;
  mainTitle: string;
  footTitle: string;
  headTitle: string;
  vatPossible: string;
}

type PaymentLanguageType = 'KO' | 'EN' | 'JA' | 'ZH' | 'ZHTW';

interface LocaleConfig {
  apiLocale: PaymentLanguageType;
  i18nKey: string;
  label: string;
}

const LOCALE_CONFIG: LocaleConfig[] = [
  { apiLocale: 'KO', i18nKey: 'ko', label: '한국어' },
  { apiLocale: 'EN', i18nKey: 'en', label: 'English' },
  { apiLocale: 'JA', i18nKey: 'jp', label: '日本語' },
  { apiLocale: 'ZH', i18nKey: 'cnsi', label: '中文（简体字）' },
  { apiLocale: 'ZHTW', i18nKey: 'cntr', label: '中文（繁体字）' },
];

const DEFAULT_LOCALE_CONFIG = LOCALE_CONFIG.find(
  item => item.apiLocale === 'EN',
)!;

const detectInitialLocaleConfig = (browserLanguage: string): LocaleConfig => {
  const browserLanguageToI18nKey: Record<string, string> = {
    ko: 'ko',
    'ko-KR': 'ko',
    ja: 'jp',
    'zh-TW': 'cntr',
    'zh-CN': 'cnsi',
    'en-US': 'en',
  };

  const i18nKey = browserLanguageToI18nKey[browserLanguage] ?? 'en';
  return (
    LOCALE_CONFIG.find(item => item.i18nKey === i18nKey) ??
    DEFAULT_LOCALE_CONFIG
  );
};

const API_HOST_MAP: Record<string, string> = {
  development: 'https://bo-api-dev.payverseglobal.com',
  stage: 'https://bo-api-stg.payverseglobal.com',
  sandbox: 'https://bo-api-snd.payverseglobal.com',
  production: 'https://bo-api.payverseglobal.com',
};

const API_HOST = API_HOST_MAP[process.env.REACT_APP_ENV ?? ''] ?? '';

const ReceiptPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const searchParams = useLocation().search;
  const paramTid = new URLSearchParams(searchParams).get('tid');

  const [selectedLocale, setSelectedLocale] = useState<PaymentLanguageType>(
    DEFAULT_LOCALE_CONFIG.apiLocale,
  );
  const [vatType, setVatType] = useState<string>('N');
  const [mid, setMid] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<TransactionReceiptResponse | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const vatOptions = [
    { value: 'Y', label: `${t('vat')}` },
    { value: 'N', label: `${t('noneVat')}` },
  ];

  useEffect(() => {
    const paramMid = new URLSearchParams(searchParams).get('mid');
    const paramResult = new URLSearchParams(searchParams).get('resultStatus');
    const sessionMid = sessionStorage.getItem('mid');
    const redirectUrl = sessionStorage.getItem('redirectUrl');

    if (paramResult !== 'SUCCESS' && redirectUrl) {
      window.location.replace(redirectUrl);
      return;
    }

    if (paramMid) {
      setMid(paramMid);
    } else if (sessionMid) {
      setMid(sessionMid);
    } else {
      navigate('/error');
      return;
    }

    const detectedConfig = detectInitialLocaleConfig(navigator.language);
    i18n.changeLanguage(detectedConfig.i18nKey);
    setSelectedLocale(detectedConfig.apiLocale);
  }, []);

  useEffect(() => {
    if (!selectedLocale || !vatType || !mid) return;

    const fetchReceipt = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_HOST}/internal/transaction/receipt/detail`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              mid: mid,
            },
            body: JSON.stringify({
              tid: paramTid,
              locale: selectedLocale,
              isVat: vatType,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('영수증 정보를 가져오는데 실패했습니다.');
        }

        const data = await response.json();
        const receiptData: TransactionReceiptResponse = data.data;

        if (!receipt && receiptData.vatPossible !== vatType) {
          setVatType(receiptData.vatPossible);
          return;
        }

        setReceipt(receiptData);
      } catch (err) {
        setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [selectedLocale, vatType, mid]);

  const handleLocaleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextApiLocale = e.target.value as PaymentLanguageType;
    const nextConfig = LOCALE_CONFIG.find(
      item => item.apiLocale === nextApiLocale,
    );

    if (nextConfig) {
      i18n.changeLanguage(nextConfig.i18nKey);
      setSelectedLocale(nextConfig.apiLocale);
    }
  };

  const captureReceipt = useCallback(async () => {
    const receiptElement = document.getElementById('receipt-content');
    if (!receiptElement) return;

    try {
      const canvas = await html2canvas(receiptElement, {
        scale: 2,
        logging: false,
        useCORS: true,
        ignoreElements: element =>
          element.classList.contains('receipt-controls'),
      });

      const link = document.createElement('a');
      link.download = `receipt-${
        receipt?.paymentSchemeInfo.orderId || 'download'
      }.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (captureError) {
      console.error('Receipt capture failed:', captureError);
    }
  }, [receipt]);

  if (loading) {
    return (
      <Container>
        <LoadingIndicator />
      </Container>
    );
  }
  if (error) return <div>{error}</div>;
  if (!receipt) return <div>영수증 정보가 없습니다.</div>;

  const formatCurrencyValue = (amount: number) => {
    return `${addCommasToNumber(amount)} ${
      receipt.paymentAmtInfo.payCurrCd === 'KRW'
        ? t('KRW')
        : receipt.paymentAmtInfo.payCurrCd
    }`;
  };

  const formatUtcOffset = (utcOffset: number) => {
    return utcOffset > 0 ? `+${utcOffset}` : String(utcOffset);
  };

  return (
    <Container>
      <div className="receipt-controls">
        <SelectWrapper>
          {receipt.vatPossible === 'Y' && (
            <Select value={vatType} onChange={e => setVatType(e.target.value)}>
              {vatOptions.map(item => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>
          )}
          <Select value={selectedLocale} onChange={handleLocaleChange}>
            {LOCALE_CONFIG.map(item => (
              <option key={item.apiLocale} value={item.apiLocale}>
                {item.label}
              </option>
            ))}
          </Select>
        </SelectWrapper>
      </div>
      <div id="receipt-content" style={{ paddingTop: 10 }}>
        <TitleWrapper>
          <Title>{receipt.headTitle}</Title>
          <LogoWrapper style={{ paddingRight: 5 }}>
            <img
              src={findImage('payverse-logo.png')}
              alt="logo"
              width="100px"
              height="16px"
            />
          </LogoWrapper>
        </TitleWrapper>
        <SecondTitle>{receipt.mainTitle}</SecondTitle>

        <SubtitleWrapper>
          <Subtitle>{receipt.paymentAmtInfo.paymentAmtInfoTitle}</Subtitle>
        </SubtitleWrapper>
        <PaymentContentWrapper>
          <SmallTitleWrapper>
            <SmallTitle>{receipt.paymentAmtInfo.transAmtSubTitle}</SmallTitle>
            <Value style={{ fontWeight: 700 }}>
              {formatCurrencyValue(receipt.paymentAmtInfo.transAmt)}
            </Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>{receipt.paymentAmtInfo.vatAmtSubTitle}</SmallTitle>
            <Value style={{ fontWeight: 700 }}>
              {formatCurrencyValue(receipt.paymentAmtInfo.vatAmt)}
            </Value>
          </SmallTitleWrapper>

          {receipt.amountDetailUse === 'Y' && (
            <>
              <SmallTitleWrapper>
                <SmallTitle>
                  {receipt.paymentAmtInfo.supplyAmtSubTitle}
                </SmallTitle>
                <Value style={{ fontWeight: 700 }}>
                  {formatCurrencyValue(receipt.paymentAmtInfo.supplyAmt)}
                </Value>
              </SmallTitleWrapper>
              <SmallTitleWrapper>
                <SmallTitle>
                  {receipt.paymentAmtInfo.taxfreeAmtSubTitle}
                </SmallTitle>
                <Value style={{ fontWeight: 700 }}>
                  {formatCurrencyValue(receipt.paymentAmtInfo.taxfreeAmt)}
                </Value>
              </SmallTitleWrapper>
              <SmallTitleWrapper>
                <SmallTitle>{receipt.paymentAmtInfo.svcAmtSubTitle}</SmallTitle>
                <Value style={{ fontWeight: 700 }}>
                  {formatCurrencyValue(receipt.paymentAmtInfo.svcAmt)}
                </Value>
              </SmallTitleWrapper>
            </>
          )}
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.paymentAmtInfo.discountAmtSubTitle}
            </SmallTitle>
            <Value style={{ fontWeight: 700 }}>
              {formatCurrencyValue(receipt.paymentAmtInfo.discountAmt)}
            </Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.paymentAmtInfo.totalPayAmtSubTitle}
            </SmallTitle>
            <Value style={{ fontWeight: 700 }}>
              {formatCurrencyValue(receipt.paymentAmtInfo.totalPayAmt)}
            </Value>
          </SmallTitleWrapper>
        </PaymentContentWrapper>

        <SubtitleWrapper>
          <Subtitle>
            {receipt.paymentSchemeInfo.paymentSchemeInfoTitle}
          </Subtitle>
        </SubtitleWrapper>
        <ContentWrapper>
          {receipt.paymentSchemeInfo.cardNo && (
            <SmallTitleWrapper>
              <SmallTitle style={{ paddingTop: 10 }}>
                {receipt.paymentSchemeInfo.cardNoSubTitle}
              </SmallTitle>
              <Value style={{ paddingTop: 15, marginBottom: 5 }}>
                {receipt.paymentSchemeInfo.cardNo}
              </Value>
            </SmallTitleWrapper>
          )}
          <SmallTitleWrapper>
            <SmallTitle>{receipt.paymentSchemeInfo.schemeSubTitle}</SmallTitle>
            <Value>{receipt.paymentSchemeInfo.schemeNm}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.paymentSchemeInfo.transactTypeSubTitle}
            </SmallTitle>
            <Value>{receipt.paymentSchemeInfo.transactType}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.paymentSchemeInfo.transactDttmSubTitle}
            </SmallTitle>
            <Value>
              {receipt.paymentSchemeInfo.transactDttm} UTC
              {formatUtcOffset(Number(receipt.paymentSchemeInfo.transactUtc))}
            </Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.paymentSchemeInfo.monthlyInstallmentSubTitle}
            </SmallTitle>
            <Value>{receipt.paymentSchemeInfo.installmentPeriod}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>{receipt.paymentSchemeInfo.appNoSubTitle}</SmallTitle>
            <Value>{receipt.paymentSchemeInfo.appNo}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>{receipt.paymentSchemeInfo.orderIdSubTitle}</SmallTitle>
            <Value>{receipt.paymentSchemeInfo.orderId}</Value>
          </SmallTitleWrapper>
        </ContentWrapper>

        <SubtitleWrapper>
          <Subtitle>{receipt.merchantInfo.merchantInfoTitle}</Subtitle>
        </SubtitleWrapper>
        <ContentWrapper>
          <SmallTitleWrapper>
            <SmallTitle>{receipt.merchantInfo.merchantNmSubTitle}</SmallTitle>
            <Value>{receipt.merchantInfo.merchantNm}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>{receipt.merchantInfo.merchantCeoSubTitle}</SmallTitle>
            <Value>{receipt.merchantInfo.merchantCeo}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.merchantInfo.merchantBiznoSubTitle}
            </SmallTitle>
            <Value>{receipt.merchantInfo.merchantBizno}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>{receipt.merchantInfo.merchantAddrSubTitle}</SmallTitle>
            <Value>
              <div style={{ width: 300, marginLeft: -60, marginTop: 7 }}>
                {receipt.merchantInfo.merchantAddr}
              </div>
            </Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.merchantInfo.merchantContractTelSubTitle}
            </SmallTitle>
            <Value>{receipt.merchantInfo.merchantContractTel}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.merchantInfo.merchantContractUrlSubTitle}
            </SmallTitle>
            <Value>{receipt.merchantInfo.merchantContractUrl}</Value>
          </SmallTitleWrapper>
        </ContentWrapper>

        <SubtitleWrapper>
          <Subtitle>{receipt.paymentGwInfo.paymentGwInfoTitle}</Subtitle>
        </SubtitleWrapper>
        <ContentWrapper>
          <SmallTitleWrapper>
            <SmallTitle>{receipt.paymentGwInfo.paymentGwNmSubTitle}</SmallTitle>
            <Value>{receipt.paymentGwInfo.paymentGwNm}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.paymentGwInfo.paymentGwCeoSubTitle}
            </SmallTitle>
            <Value>{receipt.paymentGwInfo.paymentGwCeo}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.paymentGwInfo.paymentGwBiznoSubTitle}
            </SmallTitle>
            <Value>{receipt.paymentGwInfo.paymentGwBizno}</Value>
          </SmallTitleWrapper>
          <SmallTitleWrapper>
            <SmallTitle>
              {receipt.paymentGwInfo.paymentGwAddrSubTitle}
            </SmallTitle>
            <Value>{receipt.paymentGwInfo.paymentGwAddr}</Value>
          </SmallTitleWrapper>
        </ContentWrapper>

        <ContentWrapper>
          <Description>{receipt.footTitle}</Description>
        </ContentWrapper>

        <div className="receipt-controls">
          <SelectWrapper>
            <DownloadButton onClick={captureReceipt}>
              {t('receiptDown')}
            </DownloadButton>
          </SelectWrapper>
        </div>
      </div>
    </Container>
  );
};

export default ReceiptPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 460px;
  background-color: #ffffff;
  border: 1px solid gray;
  margin: 0 auto;
  box-sizing: border-box;

  @media screen and (max-width: 460px) {
    width: 100%;
    min-width: 375px;
  }
`;

const SelectWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
  gap: 10px;
  margin: 10px 0px 10px 0px;
  padding: 0 25px;
  box-sizing: border-box;

  @media screen and (max-width: 460px) {
    gap: 10px;
  }
`;

const Select = styled.select`
  width: 200px;
  text-align: center;
  height: 25px;
  border-radius: 5px;
  border-color: lightGray;
  color: rgb(61, 61, 61);

  @media screen and (max-width: 460px) {
    width: 50%;
  }
`;

const LogoWrapper = styled.td`
  text-align: right;
`;

const Title = styled.th`
  color: #163b6a;
  font-size: 20px;
  font-weight: 700;
  text-align: left;
`;

const SecondTitle = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 15px;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #163b6a;
  letter-spacing: 0.5px;
`;

const TitleWrapper = styled.div`
  margin: 0 25px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid gray;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 25px;
  gap: 10px;
  margin-bottom: 20px;
  box-sizing: border-box;

  @media screen and (max-width: 460px) {
    margin: 0 25px;
    margin-bottom: 20px;
  }
`;

const PaymentContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 25px;
  box-sizing: border-box;

  @media screen and (max-width: 460px) {
    margin: 0;
    padding: 0 25px;
  }
`;

const SubtitleWrapper = styled.div`
  display: flex;
  padding-bottom: 3px;
  margin: 0 25px;
  border-bottom: 2px solid black;
  box-sizing: border-box;

  @media screen and (max-width: 460px) {
    margin: 0 25px 3px 25px;
  }
`;

const Subtitle = styled.div`
  color: #163b6a;
  font-size: 15px;
  font-weight: 700;
  padding-top: 15px;
  text-align: left;
  width: 100%;
`;

const SmallTitleWrapper = styled.div`
  margin-top: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SmallTitle = styled.div`
  font-weight: 700;
  text-align: left;
  padding-left: 10px;
  font-size: 13px;
  color: black;
`;

const Value = styled.div`
  display: flex;
  justify-content: center;
  font-size: 13px;
  color: black;
  max-width: 203px;
  text-align: right;
`;

const Description = styled.p`
  color: lightgray;
  font-size: 12px;
  border-top: 1px dashed lightgray;
  border-bottom: 1px solid lightgray;
  margin: 10px 25px;
  padding: 12px 0;
  text-align: center;
  font-weight: initial;

  @media screen and (max-width: 460px) {
    margin: 10px 0;
    padding: 12px 25px;
  }
`;

const DownloadButton = styled.button`
  width: 100%;
  margin: 0 25px 10px;
  padding: 8px 16px;
  background-color: #163b6a;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #0f2d52;
  }
`;
